import type { Notice, NoticeStatus } from "@/features/notices/types";
import { cn } from "@/lib/utils";

export interface ActivityEvent {
  id: string;
  noticeCode: string;
  title: string;
  /** ISO date or datetime, used for sorting + display */
  at: string;
  author: string;
  status: NoticeStatus;
}

function toDate(iso: string): Date {
  return new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
}

/**
 * Derives a newest-first activity feed from the client's notices and their
 * completed steps — there's no dedicated event log, so each notice contributes
 * a "logged" event, one event per completed step, and a "resolved" event.
 */
export function buildNoticeActivity(notices: Notice[]): ActivityEvent[] {
  const events: ActivityEvent[] = [];

  for (const n of notices) {
    const author = n.assignedPractitionerName || "—";
    events.push({
      id: `${n.id}-logged`,
      noticeCode: n.noticeCode,
      title: n.document ? "Notice uploaded and logged" : "Notice logged",
      at: n.receivedOn || n.createdAt,
      author,
      status: n.status,
    });

    for (const step of n.steps) {
      if (step.isCompleted && step.completedAt) {
        events.push({
          id: step.id,
          noticeCode: n.noticeCode,
          title: step.description,
          at: step.completedAt,
          author,
          status: n.status,
        });
      }
    }

    if (n.status === "RESOLVED") {
      events.push({
        id: `${n.id}-resolved`,
        noticeCode: n.noticeCode,
        title: `${n.noticeCode} resolved`,
        at: n.updatedAt,
        author,
        status: "RESOLVED",
      });
    }
  }

  return events.sort((a, b) => toDate(b.at).getTime() - toDate(a.at).getTime());
}

const DOT: Record<NoticeStatus, string> = {
  OPEN: "bg-primary ring-warning-soft",
  IN_PROGRESS: "bg-info ring-info-soft",
  RESOLVED: "bg-success ring-success-soft",
};

export interface NoticeActivityFeedProps {
  events: ActivityEvent[];
}

export function NoticeActivityFeed({ events }: NoticeActivityFeedProps) {
  return (
    <ol className="flex flex-col">
      {events.map((e, i) => (
        <li key={e.id} className="flex gap-3.5 py-2">
          <span className="flex shrink-0 flex-col items-center">
            <span className={cn("size-2.5 rounded-full ring-[3px]", DOT[e.status])} />
            {i < events.length - 1 && <span className="bg-border mt-1 w-0.5 flex-grow" />}
          </span>
          <span className="flex flex-col gap-px leading-snug">
            <span className="text-foreground font-semibold">{e.title}</span>
            <span className="text-muted-foreground text-xs tabular-nums">
              {e.noticeCode} ·{" "}
              {toDate(e.at).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}{" "}
              · {e.author}
            </span>
          </span>
        </li>
      ))}
      {events.length === 0 && (
        <li className="text-muted-foreground text-sm">No notice activity yet.</li>
      )}
    </ol>
  );
}
